import React, { useState, useEffect } from 'react';
import './AdminViewModal.css';

const AdminViewModal = ({ user, onClose }) => {
  const [userCompany, setUserCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && user.broker_company_id) {
      fetchUserCompany();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchUserCompany = async () => {
    try {
      const response = await fetch(`/api/companies/${user.broker_company_id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUserCompany(data);
      }
    } catch (err) {
      console.error('Failed to fetch company:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRolePermissions = (role) => {
    const permissions = {
      platform_owner: ['View all users', 'Manage all companies', 'View all deals', 'Manage commissions', 'Access platform analytics'],
      broker: ['View own company', 'Manage brokers', 'Create deals', 'View company users', 'Access company dashboard'],
      admin: ['View company data', 'Manage company settings', 'View deals', 'Access admin panel'],
      agent: ['View assigned leads', 'Create deals', 'View company dashboard', 'Submit reports'],
      introducer: ['Submit leads', 'View lead status', 'Access introducer dashboard']
    };
    return permissions[role] || [];
  };

  const getRoleDescription = (role) => {
    const descriptions = {
      platform_owner: 'Full system access - can manage everything',
      broker: 'Can manage brokers and deals for their company',
      admin: 'Administrative access to company settings',
      agent: 'Can view and work with assigned leads',
      introducer: 'Can submit leads and view their status'
    };
    return descriptions[role] || 'User role';
  };

  if (!user) return null;

  const permissions = getRolePermissions(user.role);
  const description = getRoleDescription(user.role);

  return (
    <div className="admin-view-overlay" onClick={onClose}>
      <div className="admin-view-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Admin View: {user.name}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-content">
          {/* User Info Section */}
          <section className="section">
            <h3>User Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Email</label>
                <p>{user.email}</p>
              </div>
              <div className="info-item">
                <label>Name</label>
                <p>{user.name}</p>
              </div>
              <div className="info-item">
                <label>Role</label>
                <p><span className={`role-badge role-${user.role}`}>{user.role}</span></p>
              </div>
              <div className="info-item">
                <label>Status</label>
                <p><span className={`status-badge status-${user.status}`}>{user.status}</span></p>
              </div>
              {user.phone && (
                <div className="info-item">
                  <label>Phone</label>
                  <p>{user.phone}</p>
                </div>
              )}
              <div className="info-item">
                <label>Created</label>
                <p>{new Date(user.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </section>

          {/* Role Description */}
          <section className="section">
            <h3>Role Description</h3>
            <p className="role-description">{description}</p>
          </section>

          {/* Permissions Section */}
          <section className="section">
            <h3>Permissions & Access</h3>
            <ul className="permissions-list">
              {permissions.map((permission, idx) => (
                <li key={idx}>
                  <span className="checkmark">✓</span>
                  {permission}
                </li>
              ))}
            </ul>
          </section>

          {/* Company Info (if applicable) */}
          {user.broker_company_id && (
            <section className="section">
              <h3>Associated Company</h3>
              {loading ? (
                <p>Loading...</p>
              ) : userCompany ? (
                <div className="company-info">
                  <div className="info-item">
                    <label>Company Name</label>
                    <p>{userCompany.name}</p>
                  </div>
                  <div className="info-item">
                    <label>Subscription Tier</label>
                    <p><span className="badge">{userCompany.subscription_tier}</span></p>
                  </div>
                  <div className="info-item">
                    <label>Status</label>
                    <p><span className="badge">{userCompany.subscription_status}</span></p>
                  </div>
                  {userCompany.website && (
                    <div className="info-item">
                      <label>Website</label>
                      <p><a href={userCompany.website} target="_blank" rel="noopener noreferrer">{userCompany.website}</a></p>
                    </div>
                  )}
                </div>
              ) : (
                <p>No company data available</p>
              )}
            </section>
          )}

          {/* Dashboard Preview */}
          <section className="section">
            <h3>Dashboard Preview</h3>
            <div className="dashboard-preview">
              <p className="preview-text">
                This user will see a {user.role} dashboard with access to:
              </p>
              <div className="preview-items">
                {user.role === 'platform_owner' && (
                  <>
                    <div className="preview-item">📊 Platform Overview (all companies, deals, revenue)</div>
                    <div className="preview-item">🏢 Company Management (create, edit, delete)</div>
                    <div className="preview-item">👥 User Management (create, edit, delete users)</div>
                    <div className="preview-item">💰 Commission Dashboard (all deals, commissions)</div>
                  </>
                )}
                {(user.role === 'broker' || user.role === 'owner') && (
                  <>
                    <div className="preview-item">📊 Company Dashboard (own company data)</div>
                    <div className="preview-item">👥 Broker Management (manage brokers)</div>
                    <div className="preview-item">📋 Deals (create and manage)</div>
                    <div className="preview-item">💰 Commission Tracking (own deals)</div>
                  </>
                )}
                {user.role === 'agent' && (
                  <>
                    <div className="preview-item">📋 Assigned Leads (their leads only)</div>
                    <div className="preview-item">📊 Deal Dashboard (own deals)</div>
                    <div className="preview-item">📝 Reports (activity, performance)</div>
                  </>
                )}
                {user.role === 'introducer' && (
                  <>
                    <div className="preview-item">📤 Submit Leads (lead submission form)</div>
                    <div className="preview-item">📋 My Leads (track submitted leads)</div>
                    <div className="preview-item">💰 Commissions (from accepted leads)</div>
                  </>
                )}
              </div>
            </div>
          </section>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default AdminViewModal;
